import React, { useRef } from 'react';
import { Menu } from '@carbon/react';
import { ChevronDown } from '@carbon/react/icons';
import { useShellMenuHost } from './ShellMenuHost';
import { useAttachedMenu } from './useAttachedMenu';

/** Gap in px between the trigger and the menu; matches Carbon's own MenuButton. */
const SPACING = 4;

export type HeaderSwitcherProps = {
  id: string;
  label: string;
  text: string;
  renderMark?: () => React.ReactNode;
  className?: string;
  interactive?: boolean;
  children?: React.ReactNode;
};

export const HeaderSwitcher = ({
  id,
  label,
  text,
  renderMark,
  className,
  interactive = true,
  children,
}: HeaderSwitcherProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { open, x, y, handleClick, handleMousedown, handleClose } = useAttachedMenu(triggerRef);
  const target = useShellMenuHost();

  // One option is not a choice. Render the fact, not a dropdown that can only re-pick
  // what is already picked — but keep the id, because e2e selectors depend on it and a
  // single-restaurant organization is a normal tenant, not an edge case.
  if (!interactive) {
    return (
      <span
        id={id}
        className={['shell-switcher', 'shell-switcher--static', className].filter(Boolean).join(' ')}
        title={text}
      >
        {renderMark?.()}
        <span className="cds--visually-hidden">{label}: </span>
        <span className="shell-switcher__text">{text}</span>
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={['shell-switcher', className].filter(Boolean).join(' ')}
        aria-haspopup
        aria-expanded={open}
        title={text}
        onClick={handleClick}
        onMouseDown={handleMousedown}
      >
        {renderMark?.()}
        <span className="cds--visually-hidden">{label}: </span>
        <span className="shell-switcher__text">{text}</span>
        <ChevronDown size={16} className="shell-switcher__chevron" />
      </button>
      <Menu
        label={label}
        // Carbon hides .cds--menu-item__icon unless the menu root carries this modifier,
        // and only MenuItemSelectable / MenuItemRadioGroup dispatch the context action that
        // adds it. We use plain MenuItems on purpose (those two fire onChange twice per
        // click in 1.35), so every checkmark and leading icon renders display:none without
        // this. Verified in a browser: three icon containers, each with an <svg>, all hidden.
        className="cds--menu--with-icons"
        open={open}
        onClose={handleClose}
        target={target}
        x={x}
        y={[y[0] - SPACING, y[1] + SPACING]}
        // lg is 3rem per row (Carbon's scale is xs 1.5 / sm 2 / md 2.5 / lg 3), which matches
        // the header's height and the rail's nav items, so the shell keeps one vertical
        // rhythm. sm left the 20px entity avatars looking cramped in their rows.
        size="lg"
      >
        {children}
      </Menu>
    </>
  );
};
